import type { InteractionStatus } from '../../api/types';

export type PostAnswerRecoveryStage =
  | 'idle'
  | 'answer_sent'
  | 'stream_reattach_attempt'
  | 'stream_live'
  | 'backend_processing'
  | 'awaiting_new_interaction'
  | 'completed'
  | 'failed_timeout';

export interface PostAnswerRecoveryState {
  stage: PostAnswerRecoveryStage;
  startedAtMs: number | null;
  lastProgressAtMs: number | null;
  retryCount: number;
}

export type PostAnswerRecoveryAction =
  | { type: 'start'; nowMs: number }
  | { type: 'stream_reattach_attempt'; nowMs: number }
  | { type: 'stream_live'; nowMs: number }
  | { type: 'backend_processing'; nowMs: number }
  | { type: 'progress'; nowMs: number }
  | { type: 'awaiting_new_interaction'; nowMs: number }
  | { type: 'completed'; nowMs: number }
  | { type: 'failed_timeout'; nowMs: number }
  | { type: 'reset' };

export const initialPostAnswerRecoveryState: PostAnswerRecoveryState = {
  stage: 'idle',
  startedAtMs: null,
  lastProgressAtMs: null,
  retryCount: 0,
};

export type PostAnswerRecoverySettleAction =
  | 'awaiting_new_interaction'
  | 'completed'
  | 'failed_timeout';

export type PostAnswerRecoveryUiState =
  | 'live_streaming'
  | 'backend_sync'
  | 'awaiting_user'
  | 'completed_from_persistence'
  | 'expired'
  | null;

export function getPostAnswerRecoverySettleAction(
  status: InteractionStatus | null | undefined,
): PostAnswerRecoverySettleAction {
  switch (status) {
    case 'awaiting_user':
      return 'awaiting_new_interaction';
    case 'expired':
      return 'failed_timeout';
    default:
      return 'completed';
  }
}

export function getPostAnswerRecoveryUiState(
  stage: PostAnswerRecoveryStage,
): PostAnswerRecoveryUiState {
  switch (stage) {
    case 'stream_live':
      return 'live_streaming';
    case 'answer_sent':
    case 'stream_reattach_attempt':
    case 'backend_processing':
      return 'backend_sync';
    case 'awaiting_new_interaction':
      return 'awaiting_user';
    case 'completed':
      return 'completed_from_persistence';
    case 'failed_timeout':
      return 'expired';
    case 'idle':
    default:
      return null;
  }
}

function advanceStage(
  state: PostAnswerRecoveryState,
  stage: PostAnswerRecoveryStage,
  nowMs: number,
  options: { incrementRetry?: boolean; touchProgress?: boolean } = {},
): PostAnswerRecoveryState {
  if (
    state.stage === stage &&
    !options.incrementRetry &&
    !options.touchProgress
  ) {
    return state;
  }

  return {
    stage,
    startedAtMs: state.startedAtMs ?? nowMs,
    lastProgressAtMs:
      options.touchProgress === false ? state.lastProgressAtMs : nowMs,
    retryCount: options.incrementRetry
      ? state.retryCount + 1
      : state.retryCount,
  };
}

export function postAnswerRecoveryReducer(
  state: PostAnswerRecoveryState,
  action: PostAnswerRecoveryAction,
): PostAnswerRecoveryState {
  switch (action.type) {
    case 'start':
      return {
        stage: 'answer_sent',
        startedAtMs: action.nowMs,
        lastProgressAtMs: action.nowMs,
        retryCount: 0,
      };
    case 'stream_reattach_attempt':
      return advanceStage(state, 'stream_reattach_attempt', action.nowMs, {
        incrementRetry: true,
      });
    case 'stream_live':
      return advanceStage(state, 'stream_live', action.nowMs);
    case 'backend_processing':
      return advanceStage(state, 'backend_processing', action.nowMs, {
        touchProgress: false,
      });
    case 'progress':
      if (state.stage === 'idle') {
        return state;
      }
      return {
        ...state,
        startedAtMs: state.startedAtMs ?? action.nowMs,
        lastProgressAtMs: action.nowMs,
      };
    case 'awaiting_new_interaction':
      return advanceStage(state, 'awaiting_new_interaction', action.nowMs);
    case 'completed':
      return advanceStage(state, 'completed', action.nowMs);
    case 'failed_timeout':
      return advanceStage(state, 'failed_timeout', action.nowMs, {
        touchProgress: false,
      });
    case 'reset':
      return initialPostAnswerRecoveryState;
    default:
      return state;
  }
}

export function formatRecoveryElapsedLabel(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}
